import { cannot } from '@/app/helpers'
import {
  TWhatsappActionsContent,
  TWhatsappButtonsContent,
  TWhatsappContent,
  TWhatsappFlowContent,
  TWhatsappListContent,
  TWhatsappLocationContent,
  TWhatsappMediaContent,
  TWhatsappMessageContent,
  WhatsAppLog,
} from '@/app/types'

// Local WA content type
export type WAContent =
  | {
      type: 'message'
      content: TWhatsappMessageContent
    }
  | {
      type: 'image'
      content: TWhatsappMediaContent
    }
  | {
      type: 'audio'
      content: TWhatsappMediaContent
    }
  | {
      type: 'video'
      content: TWhatsappMediaContent
    }
  | {
      type: 'document'
      content: TWhatsappMediaContent
    }
  | {
      type: 'buttons'
      content: TWhatsappButtonsContent
    }
  | {
      type: 'list'
      content: TWhatsappListContent
    }
  | {
      type: 'location'
      content: TWhatsappLocationContent
    }
  | {
      type: 'actions'
      content: TWhatsappActionsContent
    }
  | {
      type: 'flow'
      content: TWhatsappFlowContent
    }

const getFilename = (fileUrl: string) => {
  try {
    const url = new URL(fileUrl)
    return url.pathname.split('/').pop() || ''
  } catch {
    return fileUrl
  }
}

const convertFiles = (content: TWhatsappMediaContent): TWhatsappMediaContent => ({
  ...content,
  files: content.files.map(getFilename),
})

export const extractWhatsappContent = (content: TWhatsappContent): WAContent => {
  if ('message' in content) {
    return {
      type: 'message',
      content: content.message,
    }
  }

  if ('buttons' in content) {
    return {
      type: 'buttons',
      content: content.buttons,
    }
  }

  if ('list' in content) {
    return {
      type: 'list',
      content: content.list,
    }
  }

  if ('image' in content) {
    return {
      type: 'image',
      content: convertFiles(content.image),
    }
  }

  if ('audio' in content) {
    return {
      type: 'audio',
      content: convertFiles(content.audio),
    }
  }

  if ('video' in content) {
    return {
      type: 'video',
      content: convertFiles(content.video),
    }
  }

  if ('document' in content) {
    return {
      type: 'document',
      content: convertFiles(content.document),
    }
  }

  if ('location' in content) {
    return {
      type: 'location',
      content: content.location,
    }
  }

  if ('actions' in content) {
    return {
      type: 'actions',
      content: content.actions,
    }
  }

  if ('flow' in content) {
    return {
      type: 'flow',
      content: content.flow,
    }
  }

  return cannot(content)
}

export const extractWhatsappContentFromPart = (part: WhatsAppLog): WAContent => {
  if (part.content) {
    return extractWhatsappContent(part.content)
  }

  const { message, audioFiles, imageFiles, videoFiles, documentFiles, location } = part
  if (audioFiles?.length) {
    return {
      type: 'audio',
      content: {
        files: audioFiles,
        message,
      },
    }
  }
  if (imageFiles?.length) {
    return {
      type: 'image',
      content: {
        files: imageFiles,
        message,
      },
    }
  }
  if (videoFiles?.length) {
    return {
      type: 'video',
      content: {
        files: videoFiles,
        message,
      },
    }
  }
  if (documentFiles?.length) {
    return {
      type: 'document',
      content: {
        files: documentFiles,
        message,
      },
    }
  }
  if (location) {
    return {
      type: 'location',
      content: {
        label: location.label,
        address: location.address,
        latitude: location.latitude,
        longitude: location.longitude,
      },
    }
  }

  return {
    type: 'message',
    content: {
      message,
    },
  }
}
